'use client';

import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { CertificationScore } from '@/lib/api';
import { useCategoryScoringData } from '@/hooks/useCategoryScoringData';
import { useCertificationScores } from '@/hooks/useCertificationScores';
import { useScoreCalculations } from '@/hooks/useScoreCalculations';
import { useScoreStatistics } from '@/hooks/useScoreStatistics';
import { useScoreSave } from '@/contexts/ScoreSaveContext';
import { ScoreHeader } from './category-scoring/ScoreHeader';
import { CategorySection } from './category-scoring/CategorySection';

interface CategoryScoringProps {
    certificationId: string;
    cluster: 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL';
    title?: string;
    onScoreChange?: (scores: CertificationScore[]) => void;
    activeWeightProfile?: any;
}

export function CategoryScoring({ certificationId, cluster, title, onScoreChange, activeWeightProfile }: CategoryScoringProps) {
    const locale = useLocale();

    // 1. Data Loading Hook
    const {
        categoryData, allCategoryData, weightsData, allWeightsData, loading: loadingData,
        getGlossaryDefinition, toggleCategory, expandedCategories, enNameMap
    } = useCategoryScoringData(cluster, locale);

    // 2. Score Management Hook
    const {
        scores, allScores, saving, loadingScores, handleScoreChange, saveScores, hasUnsavedScores, getScoreKey
    } = useCertificationScores(certificationId, cluster, enNameMap);

    // 3. Score Calculations Hook
    const {
        getScoreColor, progress, categoryScores, clusterScore, overallScore
    } = useScoreCalculations({
        scores, allScores, categoryData, weightsData, allWeightsData, allCategoryData, enNameMap, getScoreKey, activeWeightProfile
    });

    // 4. Score Statistics Hook (for benchmark comparisons)
    const {
        getSubcategoryStats, getCategoryStats, getClusterStats, getOverallStats
    } = useScoreStatistics();

    // 5. Score Save Context - Register this cluster's save function
    const { registerSaveFunction, unregisterSaveFunction, registerHasUnsaved, unregisterHasUnsaved } = useScoreSave();

    useEffect(() => {
        registerSaveFunction(cluster, saveScores);
        return () => unregisterSaveFunction(cluster);
    }, [cluster, saveScores, registerSaveFunction, unregisterSaveFunction]);

    useEffect(() => {
        registerHasUnsaved(cluster, hasUnsavedScores);
        return () => unregisterHasUnsaved(cluster);
    }, [cluster, hasUnsavedScores, registerHasUnsaved, unregisterHasUnsaved]);

    const isLoading = loadingData || loadingScores;

    // Helper to get stats using English names (mapped from local names)
    const getCategoryStatsWrapped = (cluster: string, localCategoryName: string) => {
        const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
        const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
        return getCategoryStats(cluster, enCategoryName);
    };

    const getSubcategoryStatsWrapped = (cluster: string, localCategoryName: string, localSubcategoryName: string) => {
        const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
        const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
        const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;
        return getSubcategoryStats(cluster, enCategoryName, enSubcategoryName);
    };

    // 5. Helper function to get weights
    const getCategoryWeight = (localCategoryName: string): number => {

        // Dynamic check
        if (activeWeightProfile && activeWeightProfile.weight_tree) {
            const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                n.key === cluster || n.name_en?.toUpperCase() === cluster
            );
            if (clusterNode) {
                if (clusterNode.weighting === 0) return 0;

                const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
                const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;

                const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategoryName);
                if (categoryNode) return categoryNode.weighting;
            }
        }

        if (!weightsData) return 1;
        if (weightsData.weighting === 0) return 0;

        // Map local name to EN name for looking up in weight profile
        // The weight profile structure is always in EN
        const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
        const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName; // Fallback to local name if not found (likely EN)

        const categoryNode = weightsData.categories.find(c => c.name === enCategoryName);
        return categoryNode ? categoryNode.weighting : 1;
    };

    const getSubcategoryWeight = (localCategoryName: string, localSubcategoryName: string): number => {
        // Dynamic check
        if (activeWeightProfile && activeWeightProfile.weight_tree) {
            const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                n.key === cluster || n.name_en?.toUpperCase() === cluster
            );
            if (clusterNode) {
                if (clusterNode.weighting === 0) return 0;

                const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
                const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
                const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;

                const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategoryName);
                if (categoryNode) {
                    if (categoryNode.weighting === 0) return 0;
                    const subcategoryNode = categoryNode.children?.find((s: any) => s.name_en === enSubcategoryName);
                    if (subcategoryNode) return subcategoryNode.weighting;
                }
            }
        }

        if (!weightsData) return 1;
        if (weightsData.weighting === 0) return 0;

        const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
        const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
        const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;

        const categoryNode = weightsData.categories.find(c => c.name === enCategoryName);
        if (!categoryNode) return 1;
        if (categoryNode.weighting === 0) return 0;

        const subcategoryNode = categoryNode.subcategories.find(s => s.name === enSubcategoryName);
        return subcategoryNode ? subcategoryNode.weighting : 1;
    };

    if (isLoading) {
        return (
            <div className="flex flex-col h-[78vh]">
                {/* Skeleton header */}
                <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4 p-4 bg-slate-100 border border-slate-200 animate-pulse">
                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="h-8 w-12 bg-slate-200 mb-1" />
                            <div className="h-3 w-20 bg-slate-200" />
                        </div>
                        <div className="h-12 w-px bg-slate-200" />
                        <div>
                            <div className="h-4 w-32 bg-slate-200 mb-2" />
                            <div className="h-2 w-48 bg-slate-200" />
                        </div>
                    </div>
                </div>
                {/* Skeleton categories */}
                <div className="flex-1 overflow-hidden space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="border border-slate-200 p-4 animate-pulse">
                            <div className="h-5 w-48 bg-slate-200" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[78vh]">
            <ScoreHeader
                title={title}
                saving={saving}
                overallScore={overallScore}
                clusterScore={clusterScore}
                cluster={cluster}
                progress={progress}
                overallStats={getOverallStats()}
                clusterStats={getClusterStats(cluster)}
            />

            {/* Scrollable Categories Container */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {categoryData.map((category) => (
                    <CategorySection
                        key={category.category}
                        category={category}
                        cluster={cluster}
                        isExpanded={expandedCategories.has(category.category)}
                        toggleCategory={toggleCategory}
                        categoryScore={categoryScores[category.category]}
                        glossaryDefinition={
                            category.info
                                ? { cluster: '', terminology: category.category, definition: category.info }
                                : getGlossaryDefinition(category.category)
                        }
                        scores={scores}
                        getScoreKey={getScoreKey}
                        onScoreChange={handleScoreChange}
                        getScoreColor={getScoreColor}
                        getGlossaryDefinition={getGlossaryDefinition}
                        getCategoryStats={getCategoryStatsWrapped}
                        getSubcategoryStats={getSubcategoryStatsWrapped}
                        getCategoryWeight={getCategoryWeight}
                        getSubcategoryWeight={getSubcategoryWeight}
                    />
                ))}
            </div>
        </div>
    );
}
